import { HoverableLinkSizes } from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { useFlyoutDispatch, useFormFields } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { createSetIsAddSecondaryEmailPopupOpen } from 'src/redux/actions/app.actions'
import {
  resendVerificationCode,
  verifySecondaryEmail,
} from 'src/redux/actions/data/users.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectResendVerificationCodeLoadingFromState,
  selectResendVerificationCodeTopLevelErrorFromState,
  selectVerifySecondaryEmailLoadingFromState,
  selectVerifySecondaryEmailTopLevelErrorFromState,
} from 'src/redux/selectors/data/secondaryEmails.selectors'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type SecondaryEmailFlatFields = {
  email?: string
}

export type SecondaryEmailVerificationFlatFields = {
  verificationCode?: string
}

interface VerifySecondaryEmailFormProps {
  className?: string
  secondaryEmail: string
}

const VerifySecondaryEmailForm: React.FC<VerifySecondaryEmailFormProps> = (
  props,
) => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const resendVerificationCodeLoading = useSelector(
    selectResendVerificationCodeLoadingFromState,
  )
  const resendVerificationCodeTopLevelError = useSelector(
    selectResendVerificationCodeTopLevelErrorFromState,
  )
  const verifySecondaryEmailLoading = useSelector(
    selectVerifySecondaryEmailLoadingFromState,
  )
  const verifySecondaryEmailTopLevelError = useSelector(
    selectVerifySecondaryEmailTopLevelErrorFromState,
  )

  const { analytics } = useAnalyticsContext()
  const dispatch = useFlyoutDispatch()

  const {
    fields: secondaryEmailVerificationFlatFields,
    fieldsErrors: secondaryEmailVerificationFlatFieldsErrors,
    onFieldChange: onSecondaryEmailVerificationFlatFieldChange,
    setErrorsAndReturnDidPassValidation:
      secondaryEmailVerificationFlatFieldsSetErrors,
  } = useFormFields<SecondaryEmailVerificationFlatFields>(
    {},
    () => ({
      verificationCode: [stringIsEmpty()],
    }),
    undefined,
  )

  const verifyCodeAndSaveEmail = (): void => {
    if (
      secondaryEmailVerificationFlatFieldsSetErrors() &&
      secondaryEmailVerificationFlatFields.verificationCode
    ) {
      analytics.track('secondary email verification - verify code')
      dispatch(
        verifySecondaryEmail(
          props.secondaryEmail,
          secondaryEmailVerificationFlatFields.verificationCode,
        ),
      )
    }
  }

  return (
    <>
      <FieldLabel>
        <Trns source={'Secondary Email'} />
      </FieldLabel>
      {props.secondaryEmail}
      <TextInputField
        css={css`
          margin-top: 20px;
        `}
        fieldLabel={
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
              width: 100%;
            `}
          >
            <Trns source={'Verification Code'} />
            <HoverableLink
              underline
              regular
              size={HoverableLinkSizes.Medium}
              ariaLabelSource={'Resend code'}
              onClick={(): void => {
                analytics.track('secondary email verification - resend code')
                dispatch(resendVerificationCode(props.secondaryEmail))
              }}
              disabled={resendVerificationCodeLoading}
            >
              <Trns source={'Resend code'} />
            </HoverableLink>
          </div>
        }
        placeholder={translateSingleString(locale, 'Enter code')}
        design={TextInputFieldDesigns.New}
        onChange={(val: string): void =>
          onSecondaryEmailVerificationFlatFieldChange('verificationCode', val)
        }
        errors={secondaryEmailVerificationFlatFieldsErrors['verificationCode']}
      />
      {resendVerificationCodeTopLevelError && (
        <ErrorList
          errors={[resendVerificationCodeTopLevelError]}
          css={css`
            margin-top: 8px;
          `}
        />
      )}
      <div
        css={css`
          margin-top: 40px;
          display: flex;
          gap: 20px;
          align-items: center;
        `}
      >
        <Button
          type={ButtonTypes.button}
          design={ButtonDesigns.designCta}
          big={true}
          text={<Trns source={'Verify Code and Save Email'} />}
          onClick={verifyCodeAndSaveEmail}
          loading={verifySecondaryEmailLoading}
        />
        <HoverableLink
          underline
          ariaLabelSource={'Cancel'}
          onClick={(): void => {
            analytics.track('secondary email verification - cancel')
            dispatch(createSetIsAddSecondaryEmailPopupOpen(false))
          }}
        >
          <Trns source={'Cancel'} />
        </HoverableLink>
      </div>
      {verifySecondaryEmailTopLevelError && (
        <ErrorList
          errors={[verifySecondaryEmailTopLevelError]}
          css={css`
            margin-top: 8px;
          `}
        />
      )}
    </>
  )
}

export default VerifySecondaryEmailForm
